import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createVNode as _createVNode, createTextVNode as _createTextVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex justify-center items-center h-full"
}
const _hoisted_2 = {
  key: 1,
  class: "flex flex-col justify-center items-center h-full"
}
const _hoisted_3 = { class: "text-red-500 mb-4" }
const _hoisted_4 = {
  key: 2,
  class: "flex flex-col h-full justify-between"
}
const _hoisted_5 = { class: "space-y-6" }
const _hoisted_6 = { class: "flex justify-between items-start mb-2 w-full" }
const _hoisted_7 = { class: "space-y-2" }
const _hoisted_8 = { class: "text-2xl font-bold text-gray-800" }
const _hoisted_9 = { class: "flex items-center space-x-2" }
const _hoisted_10 = {
  key: 0,
  class: "text-sm text-gray-500"
}
const _hoisted_11 = {
  key: 0,
  class: "flex justify-end gap-4"
}
const _hoisted_12 = { class: "mb-6" }
const _hoisted_13 = { class: "text-xl font-bold mb-2" }
const _hoisted_14 = { class: "text-sm text-gray-600" }
const _hoisted_15 = {
  key: 1,
  class: "p-4 bg-blue-50 rounded-md border border-blue-200"
}
const _hoisted_16 = { class: "text-sm text-blue-700" }
const _hoisted_17 = { key: 2 }
const _hoisted_18 = {
  key: 0,
  class: "mb-8"
}
const _hoisted_19 = { key: 1 }
const _hoisted_20 = {
  key: 5,
  class: "text-center py-8 text-gray-500"
}
const _hoisted_21 = ["disabled"]
const _hoisted_22 = {
  key: 1,
  class: "w-full text-center"
}
const _hoisted_23 = ["disabled"]
const _hoisted_24 = {
  key: 0,
  class: "flex items-center justify-center"
}
const _hoisted_25 = { key: 1 }

import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { ChallengeConfiguration } from "@/types/v2/task/challenge/configuration.types";
import { useChallenge } from "@/composables/tasks/challenge/useChallenge";
import { useChallengeConfigurations } from "@/composables/tasks/challenge/useChallengeConfigurations";
import { useTask } from "@/composables/tasks/useTask";
import { useNotifications } from "@/composables/useNotifications";
import { TasksService } from "@/services/v2/tasks";
import { useEquipment } from "@/composables/tasks/useEquipment";

import FooterActions from "@/components/v2/Tasks/Challenge/Buttons/ActionButtons/FooterActions.vue";
import ConfigurationsTable from "@/components/v2/Tasks/Challenge/Tables/ConfigurationsTable.vue";
import BathConfigurationsTable from "@/components/v2/Tasks/Challenge/Tables/BathConfigurationsTable.vue";
import HistoryList from "@/components/v2/Tasks/Challenge/Lists/HistoryList.vue";
import CommentsModal from "@/components/v2/Modal/CommentsModal.vue";
import InstructionsModal from "@/components/v2/Modal/InstructionsModal.vue";
import WeightModal from "@/components/v2/Tasks/Challenge/Modals/WeightModal.vue";
import WeightIcon from "@/components/v2/UI/Icons/WeightIcon.vue";
import DocumentIcon from "@/components/v2/UI/Icons/DocumentIcon.vue";
import InfoIcon from "@/components/v2/UI/Icons/InfoIcon.vue";
import { useWeight } from "@/composables/tasks/challenge/useChallengeWeight";
import RandomWeightProgressCard from "@/components/v2/Tasks/Shared/RandomWeightProgressCard.vue";
import TaskCompletionBlockedModal from "@/components/v2/Tasks/Task/TaskCompletionBlockedModal.vue";

// ===================================================================
// Composables
// ===================================================================

export default /*@__PURE__*/_defineComponent({
  __name: 'ConfigurationsView',
  setup(__props) {

const router = useRouter();
const { activeTask } = useTask();
const { 
    tanks, 
    isLoading, 
    error, 
    ensureTaskLoaded,
    vaccineMethods, 
    challengeMaterials, 
    dilutions, 
    preChallengeTask,
 
} = useChallenge();
const {
    configurations,
    standardConfigurations,
    bathConfigurations,
    isBathTask,
    isStandardTask,
    isMixedTask,

} = useChallengeConfigurations();
const { showTaskCompletionSuccess, showError } = useNotifications();
const {
    isIndividualWeightType,
    isNoWeightType,
    isRandomWeightType,
    getWeighedFish,
    getTargetWeightAmount
} = useWeight();
const { getWeightIdCompletionValidation } = useEquipment();

// ===================================================================
// Modal State
// ===================================================================
const selectedConfiguration = ref<ChallengeConfiguration | null>(null);
const weightModal = ref<boolean>(false);

// ===================================================================
// Modal Handlers
// ===================================================================
const openWeightModal = (config?: ChallengeConfiguration | null) => {
    weightModal.value = true;
    selectedConfiguration.value = config || null;
};

const goToEquipmentManagement = () => {
    router.push({ name: "ChallengeEquipmentView" });
};

// ===================================================================
// Computed Properties
// ===================================================================
const taskTypeLabel = computed(() => {
    if (isMixedTask.value) return 'Mixed Challenge (Bath + Injection)';
    if (isBathTask.value) return 'Bath Challenge';
    return 'Injection Challenge';
});

const taskDescription = computed(() => {
    if (isBathTask.value) {
        return 'Review bath challenge configurations. Processes are time-based with specified durations. You can start and finish each bath process individually.';
    }
    return 'You can make minor adjustments to tag colors, destination tanks, and destination groups if needed. All changes require justification before proceeding.';
});

// ===================================================================
// State
// ===================================================================
const instructionsModal = ref(false);
const commentsModal = ref(false);
const isFinishing = ref(false);
const completionBlockModal = ref(false);

// ===================================================================
// Error Handling
// ===================================================================
const handleRetry = () => {
    ensureTaskLoaded();
};

// ===================================================================
// Actions
// ===================================================================
// Open instructions modal
const toggleInstructionsModal = () => {
    instructionsModal.value = !instructionsModal.value;
};

// Open comments modal
const toggleCommentsModal = () => {
    commentsModal.value = !commentsModal.value;
};

// Next step
const handleProceed = () => {
    if (isBathTask.value) {
        // For bath tasks, check if all processes are started/completed
        // You might want different logic here
        router.push({ name: 'ChallengeChallenges' });
    } else {
        router.push({ name: 'ChallengeChallenges' });
    }
};

// Check if we can proceed (for bath tasks, might want to check process status)
const canProceed = computed(() => {
    if (isBathTask.value) {
        // For demo purposes, always allow proceed
        // In real implementation, you might check if processes are ready
        return true;
    }
    return tanks.value?.length > 0;
});
const completionValidation = computed(() => getWeightIdCompletionValidation());



// Handle finishing the task
const handleFinishTask = async () => {
    if (!activeTask.value) return;

    const validation = getWeightIdCompletionValidation();
    if (!validation.isValid) {
        completionBlockModal.value = true;
        return;
    }
    
    isFinishing.value = true;
    
    try {
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
        console.error('Error completing challenge task:', error);
        showError("Task Completion Failed", "Failed to complete the challenge task. Please try again.");
    } finally {
        isFinishing.value = false;
    }
};

return (_ctx: any,_cache: any) => {
  return (_unref(isLoading))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, _cache[4] || (_cache[4] = [
        _createElementVNode("div", { class: "animate-spin rounded-full h-12 w-12 border-b-2 border-primary" }, null, -1)
      ])))
    : (_unref(error))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(error)), 1),
          _createElementVNode("button", {
            onClick: handleRetry,
            class: "px-4 py-2 bg-primary text-white rounded"
          }, "Retry")
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("h1", _hoisted_8, _toDisplayString(_unref(activeTask)?.title), 1),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium", {
                            'bg-blue-100 text-blue-800': _unref(isBathTask),
                            'bg-orange-100 text-orange-800': _unref(isStandardTask) && !_unref(isBathTask),
                            'bg-purple-100 text-purple-800': _unref(isMixedTask)
                            }])
                  }, _toDisplayString(taskTypeLabel.value), 3),
                  (_unref(configurations).length > 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(_unref(configurations).length) + " configuration" + _toDisplayString(_unref(configurations).length !== 1 ? 's' : ''), 1))
                    : _createCommentVNode("", true)
                ])
              ]),
              (_unref(isBathTask) || _unref(isMixedTask))
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("button", {
                      onClick: goToEquipmentManagement,
                      class: "px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm"
                    }, [
                      _createVNode(InfoIcon),
                      _cache[5] || (_cache[5] = _createElementVNode("span", null, "Equipment", -1))
                    ]),
                    (!_unref(isIndividualWeightType) && !_unref(isNoWeightType))
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (openWeightModal())),
                          class: "px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm"
                        }, [
                          _createVNode(WeightIcon),
                          _cache[6] || (_cache[6] = _createTextVNode(" Record Weight "))
                        ]))
                      : _createCommentVNode("", true),
                    _createElementVNode("button", {
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(router).push({name: 'ChallengeWeights'}))),
                      class: "px-4 py-2 bg-primary text-white flex items-center gap-2 rounded-lg shadow-sm hover:bg-primary/90 transition-colors duration-200 font-medium text-sm"
                    }, [
                      _createVNode(DocumentIcon),
                      _cache[7] || (_cache[7] = _createTextVNode(" View Weight Records "))
                    ])
                  ]))
                : _createCommentVNode("", true)
            ]),
            (_unref(isRandomWeightType))
              ? (_openBlock(), _createBlock(RandomWeightProgressCard, {
                  key: 0,
                  class: "sticky top-2 z-10",
                  current: _unref(getWeighedFish),
                  target: _unref(getTargetWeightAmount)
                }, null, 8, ["current", "target"]))
              : _createCommentVNode("", true),
            _cache[12] || (_cache[12] = _createElementVNode("span", { class: "w-full bg-secondary h-[1px] block" }, null, -1)),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("h2", _hoisted_13, _toDisplayString(_unref(isBathTask) ? 'Bath Process Management' : 'Editing before starting task'), 1),
              _createElementVNode("p", _hoisted_14, _toDisplayString(taskDescription.value), 1)
            ]),
            (_unref(preChallengeTask))
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _cache[8] || (_cache[8] = _createElementVNode("h3", { class: "text-lg font-medium text-blue-800" }, "Dilutions Task", -1)),
                  _createElementVNode("p", _hoisted_16, [
                    _createTextVNode(" This challenge is linked to dilutions task: " + _toDisplayString(_unref(preChallengeTask).title) + " ", 1),
                    _createElementVNode("span", {
                      class: _normalizeClass(["ml-2 px-2 py-1 text-xs rounded-full", {
                          'bg-green-100 text-green-800': _unref(preChallengeTask).status === 'completed',
                          'bg-yellow-100 text-yellow-800': _unref(preChallengeTask).status === 'in_progress',
                          'bg-gray-100 text-gray-800': _unref(preChallengeTask).status === 'pending'
                        }])
                    }, _toDisplayString(_unref(preChallengeTask).status), 3)
                  ])
                ]))
              : _createCommentVNode("", true),
            _createVNode(HistoryList),
            (_unref(isMixedTask))
              ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                  (_unref(standardConfigurations).length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                        _cache[9] || (_cache[9] = _createElementVNode("h3", { class: "text-lg font-medium text-gray-900 mb-4" }, "Injection Configurations", -1)),
                        _createVNode(ConfigurationsTable, {
                          configurations: _unref(standardConfigurations),
                          vaccineMethods: _unref(vaccineMethods),
                          challengeMaterials: _unref(challengeMaterials),
                          dilutions: _unref(dilutions),
                          tanks: _unref(tanks)
                        }, null, 8, ["configurations", "vaccineMethods", "challengeMaterials", "dilutions", "tanks"])
                      ]))
                    : _createCommentVNode("", true),
                  (_unref(bathConfigurations).length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                        _cache[10] || (_cache[10] = _createElementVNode("h3", { class: "text-lg font-medium text-gray-900 mb-4" }, "Bath Configurations", -1)),
                        _createVNode(BathConfigurationsTable, {
                          configurations: _unref(bathConfigurations),
                          vaccineMethods: _unref(vaccineMethods),
                          tanks: _unref(tanks),
                          onOpenWeightModal: openWeightModal
                        }, null, 8, ["configurations", "vaccineMethods", "tanks"])
                      ]))
                    : _createCommentVNode("", true)
                ]))
              : (_unref(isBathTask) && _unref(tanks)?.length)
                ? (_openBlock(), _createBlock(BathConfigurationsTable, {
                    key: 3,
                    configurations: _unref(bathConfigurations),
                    vaccineMethods: _unref(vaccineMethods),
                    tanks: _unref(tanks),
                    onOpenWeightModal: openWeightModal
                  }, null, 8, ["configurations", "vaccineMethods", "tanks"]))
                : (_unref(isStandardTask) && _unref(tanks)?.length)
                  ? (_openBlock(), _createBlock(ConfigurationsTable, {
                      key: 4,
                      configurations: _unref(standardConfigurations),
                      vaccineMethods: _unref(vaccineMethods),
                      challengeMaterials: _unref(challengeMaterials),
                      dilutions: _unref(dilutions),
                      tanks: _unref(tanks)
                    }, null, 8, ["configurations", "vaccineMethods", "challengeMaterials", "dilutions", "tanks"]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_20, _cache[11] || (_cache[11] = [
                      _createElementVNode("p", null, "No configurations available", -1)
                    ])))
          ]),
          _createVNode(FooterActions, null, {
            start: _withCtx(() => [
              _createElementVNode("button", {
                onClick: toggleInstructionsModal,
                class: "w-full"
              }, " Instructions ")
            ]),
            middle: _withCtx(() => [
              _createElementVNode("button", {
                onClick: toggleCommentsModal,
                class: "w-full"
              }, " Comments ")
            ]),
            end: _withCtx(() => [
              (!_unref(isBathTask))
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    onClick: handleProceed,
                    disabled: !canProceed.value,
                    class: _normalizeClass(["w-full", {
                'opacity-50 cursor-not-allowed': !canProceed.value
            }])
                  }, " Proceed ", 10, _hoisted_21))
                : (_openBlock(), _createElementBlock("div", _hoisted_22, [
                    _createElementVNode("button", {
                      onClick: handleFinishTask,
                      disabled: isFinishing.value,
                      class: _normalizeClass(["w-full relative", { 'opacity-50 cursor-not-allowed': isFinishing.value }])
                    }, [
                      (isFinishing.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_24, _cache[13] || (_cache[13] = [
                            _createElementVNode("svg", {
                              class: "animate-spin -ml-1 mr-2 h-4 w-4 text-white",
                              xmlns: "http://www.w3.org/2000/svg",
                              fill: "none",
                              viewBox: "0 0 24 24"
                            }, [
                              _createElementVNode("circle", {
                                class: "opacity-25",
                                cx: "12",
                                cy: "12",
                                r: "10",
                                stroke: "currentColor",
                                "stroke-width": "4"
                              }),
                              _createElementVNode("path", {
                                class: "opacity-75",
                                fill: "currentColor",
                                d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              })
                            ], -1),
                            _createTextVNode(" Finishing... ")
                          ])))
                        : (_openBlock(), _createElementBlock("span", _hoisted_25, " Finish Task "))
                    ], 10, _hoisted_23)
                  ]))
            ]),
            _: 1
          }),
          _createVNode(InstructionsModal, {
            "show-modal": instructionsModal.value,
            "toggle-instructions-modal": toggleInstructionsModal
          }, null, 8, ["show-modal"]),
          _createVNode(CommentsModal, {
            "show-modal": commentsModal.value,
            "toggle-comments-modal": toggleCommentsModal
          }, null, 8, ["show-modal"]),
          (weightModal.value)
            ? (_openBlock(), _createBlock(WeightModal, {
                key: 0,
                show: weightModal.value,
                configuration: selectedConfiguration.value,
                onClose: _cache[2] || (_cache[2] = ($event: any) => (weightModal.value = false))
              }, null, 8, ["show", "configuration"]))
            : _createCommentVNode("", true),
          _createVNode(TaskCompletionBlockedModal, {
            show: completionBlockModal.value,
            reason: completionValidation.value.reason,
            onClose: _cache[3] || (_cache[3] = ($event: any) => (completionBlockModal.value = false))
          }, null, 8, ["show", "reason"])
        ]))
}
}

})